import { instanceKey, instanceOf } from "../funnel/order";
import { screenOf } from "../routes/registry";
import { closedKey, isFixed, pageOf, pagePath } from "./registry";
import type { SitePage } from "./registry";
import { stepPageOf } from "./stepPages";

/**
 * ═══ UNE PAGE QUE L'ORGANISATEUR A FERMÉE ═══
 *
 * Voir `closedKey` dans le registre pour le réglage. Ce fichier dit quelle page sert une
 * adresse, et si elle est fermée. La garde, elle, reste au châssis (`Shell`), qui seul
 * connaît le mode : l'organisateur ouvre une page fermée pour la régler.
 *
 * ⚠️ CE N'EST PAS UNE GARDE DE SÉCURITÉ. Fermer une page retire un écran aux
 * participants ; les données qu'il lit restent servies par l'API à qui y a droit.
 */

/** Fermée ? `read` est la lecture d'un réglage. Les pages fixes ne se ferment pas. */
export function isClosed(
  page: Pick<SitePage, "key">,
  read: (key: string) => unknown,
): boolean {
  return !isFixed(page.key) && read(closedKey(page)) === true;
}

/**
 * La clé de la page que sert cette adresse (page du site, exemplaire ajouté, ou étape
 * servie en page), `undefined` pour tout le reste (l'accueil, le tunnel, la connexion,
 * les outils du direct).
 *
 * ⚠️ L'EXEMPLAIRE SUFFIXE, comme partout : fermer la section 2 ne ferme pas la 1.
 * ⚠️ ET L'ÉTAPE SERVIE EN PAGE N'EST PAS L'ÉTAPE : `/informations` se ferme,
 * `/registration/informations` reste au tunnel, qui a ses propres interrupteurs.
 */
export function pageKeyAt(pathname: string): string | undefined {
  const step = stepPageOf(pathname);
  if (step) return step.key;
  const page = pageOf(screenOf(pathname)?.key);
  if (!page) return undefined;
  return instanceKey(
    page.key,
    page.repeatable ? instanceOf(pathname, pagePath(page)) : undefined,
  );
}

/** L'adresse mène-t-elle à une page fermée ? */
export function isClosedAt(
  pathname: string,
  read: (key: string) => unknown,
): boolean {
  const key = pageKeyAt(pathname);
  return !!key && isClosed({ key }, read);
}
